export const PARTY_PET_CREATURES = [
  "blob", "sprout_frog", "tiny_dino", "party_bird", "fluffball", "octopop", "ghost", "turtle",
  "tiny_dragon", "robot", "alien", "mushroom", "star_cat", "cake", "cloud", "raccoon",
] as const;

export type PartyPetCreature = typeof PARTY_PET_CREATURES[number];
export type PartyPetTone = "family" | "standard" | "adult";
export type PartyPetTheme = "playroom" | "disco" | "garden" | "space";
export type PartyPetActionKind = "idle" | "walk" | "eat" | "sleep" | "dance" | "play" | "visit" | "gift" | "prank" | "celebrate" | "shocked";
export type PartyPetMinigameKind = "pet_race" | "dance_battle" | "balloon_pop" | "cake_grab" | "musical_chairs" | "fashion_show" | "pet_talent_show" | "hot_potato" | "simon_says" | "reaction_race" | "snack_catch" | "majority_rules" | "chaos_round";

export type PartyPetMission = {
  id: string;
  text: string;
  kind: string;
  target: number;
  progress: number;
  realWorld: boolean;
  safety: string;
};

export type PartyPetStats = {
  foodsEaten: number;
  cakeEaten: number;
  giftsGiven: number;
  giftsReceived: number;
  interactions: number;
  dances: number;
  naps: number;
  pranks: number;
  minigameWins: number;
  minigameParticipation: number;
  balloonsPopped: number;
  outfitsWorn: number;
  missionsCompleted: number;
  realWorldMissionsCompleted: number;
  friendshipsFormed: number;
  rivalriesFormed: number;
  timesPhotobombed: number;
  cakeTheftAttempts: number;
};

export type PartyPetRelationship = {
  familiarity: number;
  friendship: number;
  rivalry: number;
  admiration: number;
  annoyance: number;
  trust: number;
  label: string;
};

export type PartyPet = {
  sittingOut?: boolean;
  id: string;
  playerId: string;
  name: string;
  creature: PartyPetCreature;
  color: string;
  createdAt: number;
  lastActionAt: number;
  lastPlayerActionAt: number;
  mood: string;
  hunger: number;
  energy: number;
  fun: number;
  social: number;
  chaos: number;
  coins: number;
  inventory: string[];
  equipped: string[];
  traits: Record<string, number>;
  relationships: Record<string, PartyPetRelationship>;
  achievements: string[];
  evolution: string | null;
  mission: PartyPetMission | null;
  lastMissionClaimAt: number;
  action: { kind: PartyPetActionKind; label: string; targetPetId: string | null; x: number; y: number; until: number };
  statistics: PartyPetStats;
  favoriteFood: string | null;
  lastNotableEvent: string;
};

export type PartyPetEvent = {
  id: string;
  type: string;
  text: string;
  actorPetId?: string;
  targetPetId?: string;
  object?: string;
  createdAt: number;
  major?: boolean;
};

export type PartyPetMinigame = {
  id: string;
  kind: PartyPetMinigameKind;
  title: string;
  instruction: string;
  startedAt: number;
  endsAt: number;
  scores: Record<string, number>;
  inputCounts: Record<string, number>;
  lastInputAt: Record<string, number>;
  selections: Record<string, string>;
  roundClaims: Record<string, string>;
  seed: number;
  winnerIds: string[];
  resultText: string;
};

export type PartyPetSettings = {
  theme: PartyPetTheme;
  tone: PartyPetTone;
  eventPaceMinutes: number;
  minigameFrequency: "low" | "normal" | "high";
  soundEnabled: boolean;
  realWorldMissions: boolean;
  relationshipsEnabled: boolean;
  mischiefEnabled: boolean;
  evolutionsEnabled: boolean;
};

export type PartyPetsState = {
  sessionId: string;
  status: "setup" | "live" | "minigame" | "finale" | "ended";
  startedAt: number | null;
  settings: PartyPetSettings;
  pets: Record<string, PartyPet>;
  events: PartyPetEvent[];
  careMeter: number;
  reachedMilestones: number[];
  nextAutonomyAt: number;
  nextPartyEventAt: number;
  currentMinigame: PartyPetMinigame | null;
  awards: Array<{ title: string; petIds: string[]; reason: string }>;
  finalPhotoAt: number | null;
};

export type PartyPetsPlayers = Record<string, { id: string; name: string; connected: boolean }>;

const FOOD_PRICES: Record<string, number> = { pizza: 10, cake: 12, tacos: 10, "mystery snack": 8 };
const TOY_PRICES: Record<string, number> = { ball: 8, bubbles: 8, karaoke: 12, balloons: 10 };
const DRESS_REWARD = 4;
const FOOD = Object.keys(FOOD_PRICES);
const TOYS = Object.keys(TOY_PRICES);
const COSMETICS = ["party hat", "crown", "sunglasses", "bow tie", "tiara", "cowboy hat", "wizard hat", "chef hat", "pirate hat", "tiny top hat", "superhero cape", "party glasses", "mustache", "oversized shoes"];
const GIFTS = ["snack", "toy", "flower", "hat", "balloon", "mystery box", "trophy", "rubber chicken"];
const MINIGAME_TITLES: Record<PartyPetMinigameKind, [string, string, number]> = {
  pet_race: ["Pet Race", "Alternate LEFT and RIGHT to sprint. Repeating one paw makes your pet stumble.", 32_000],
  dance_battle: ["Dance Battle", "Match the glowing dance pad before the beat changes.", 35_000],
  balloon_pop: ["Balloon Pop", "Find and pop the gold-star balloon in each new bunch.", 30_000],
  cake_grab: ["Treat Grab", "Wait for the treat to appear, then grab it quickly. Early grabs lose points.", 28_000],
  musical_chairs: ["Musical Chairs", "Dance while the music plays. Choose a chair only when the music stops.", 28_000],
  fashion_show: ["Fashion Show", "Pick the most magnificent look for the runway.", 35_000],
  pet_talent_show: ["Pet Talent Show", "Choose a ridiculous talent. The pets handle show business.", 32_000],
  hot_potato: ["Hot Toy", "Pass the toy in the arrow's direction before it changes.", 28_000],
  simon_says: ["Simon Says", "Tap the matching move only when SIMON SAYS appears.", 32_000],
  reaction_race: ["Reaction Race", "Ignore READY, WAIT, and fake-outs. React only on GO!", 26_000],
  snack_catch: ["Snack Catch", "Choose the falling snack's lane. Avoid the purple yuck snacks.", 30_000],
  majority_rules: ["Majority Rules", "Vote: which pet is most likely to steal the snacks?", 25_000],
  chaos_round: ["Chaos Round", "Choose one secret action. Everything happens at once.", 25_000],
};

const MISSIONS = [
  ["gift_three", "Send three gifts to other pets.", "giftsGiven", 3],
  ["dance_three", "Dance three times without losing your dignity.", "dances", 3],
  ["snack_tour", "Try three different foods.", "foodsEaten", 3],
  ["socialize", "Interact with four other pets.", "interactions", 4],
  ["fashion", "Wear three ridiculous accessories.", "outfitsWorn", 3],
  ["prank", "Pull off two harmless pranks.", "pranks", 2],
] as const;

export const PARTY_PET_REAL_WORLD_MISSIONS = [
  "Introduce yourself to someone nearby you have not met yet. First names only—no contact details.",
  "Ask someone outside your usual group to invent a three-second, no-touch dance move. Try it together only if they want to.",
  "Ask someone you do not know yet what snack they would choose for a virtual pet.",
  "Give a sincere, non-appearance-based compliment to someone new—try their laugh, energy, or music choice.",
  "Show your pet to someone you have not met and ask them to choose its next accessory.",
  "Learn one harmless fun fact from someone new, then share one about yourself.",
  "Offer to help with one small shared-space task—straighten napkins, carry an empty plate, or tidy a shared table.",
  "Do a ten-second solo celebration dance in an open public area. Invite others to copy it, but make joining completely optional.",
  "Ask someone new which song would instantly improve the moment. No phone number exchange required.",
  "Find one shared interest with a person you have just met.",
  "Ask a new person to name your pet's imaginary signature dance move.",
  "Invite someone who seems open to chatting into a group conversation. Let a 'no thanks' end the mission gracefully.",
  "Ask someone new whether they would pick teleportation or invisibility, and why.",
  "Have someone you just met choose: tiny dragon, ghost, or trash-prince raccoon.",
  "Ask a willing new acquaintance for their best one-line advice for taking care of a virtual pet.",
  "Find someone outside your group who wants to do a five-second air-high-five victory pose—no photo or touching needed.",
  "Lead a tiny round of applause for someone who helped make the shared space welcoming.",
] as const;

const REAL_WORLD_SAFETY = "Ask once, stay in open public areas, accept no immediately, and never request photos, touching, drinks, contact details, or personal information. Minors should participate only with a trusted adult.";

function cosmeticSlot(item: string) {
  if (item === "superhero cape") return "back";
  if (item === "oversized shoes") return "feet";
  if (item === "bow tie") return "neck";
  if (item === "mustache") return "face";
  if (item.includes("glasses") || item === "sunglasses") return "eyes";
  return "head";
}

function equipCosmetic(pet: PartyPet, item: string) {
  const slot = cosmeticSlot(item);
  pet.equipped = [...pet.equipped.filter((entry) => cosmeticSlot(entry) !== slot), item];
}

function normalizeEquipped(items: string[]) {
  const equipped: string[] = [];
  for (const item of items) {
    const slot = cosmeticSlot(item);
    const previous = equipped.findIndex((entry) => cosmeticSlot(entry) === slot);
    if (previous >= 0) equipped.splice(previous, 1);
    equipped.push(item);
  }
  return equipped;
}

function id(prefix: string, now = Date.now()) {
  return `${prefix}-${now.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function clamp(value: number, minimum = 0, maximum = 100) {
  return Math.max(minimum, Math.min(maximum, Math.round(value)));
}

function clean(value: unknown, maximum: number) {
  return typeof value === "string" ? value.trim().replace(/\s+/g, " ").replace(/[<>]/g, "").slice(0, maximum) : "";
}

function freshStatistics(): PartyPetStats {
  return { foodsEaten: 0, cakeEaten: 0, giftsGiven: 0, giftsReceived: 0, interactions: 0, dances: 0, naps: 0, pranks: 0, minigameWins: 0, minigameParticipation: 0, balloonsPopped: 0, outfitsWorn: 0, missionsCompleted: 0, realWorldMissionsCompleted: 0, friendshipsFormed: 0, rivalriesFormed: 0, timesPhotobombed: 0, cakeTheftAttempts: 0 };
}

function activePartyPets(state: PartyPetsState) {
  return Object.values(state.pets).filter((pet) => !pet.sittingOut);
}

export function createInitialPartyPetsState(now = Date.now()): PartyPetsState {
  return {
    sessionId: id("party", now), status: "setup", startedAt: null,
    settings: { theme: "playroom", tone: "standard", eventPaceMinutes: 12, minigameFrequency: "normal", soundEnabled: true, realWorldMissions: true, relationshipsEnabled: true, mischiefEnabled: true, evolutionsEnabled: true },
    pets: {}, events: [], careMeter: 0, reachedMilestones: [], nextAutonomyAt: now + 12_000, nextPartyEventAt: now + 12 * 60_000,
    currentMinigame: null, awards: [], finalPhotoAt: null,
  };
}

export function hydratePartyPetsState(value: Partial<PartyPetsState> | undefined, now = Date.now()): PartyPetsState {
  const fresh = createInitialPartyPetsState(now);
  if (!value) return fresh;
  const legacy = value as unknown as { birthdayMeter?: number; settings?: { theme?: unknown } };
  const settings = { ...fresh.settings, ...(value.settings || {}) };
  if (String(legacy.settings?.theme || "") === "birthday_bash") settings.theme = "playroom";
  const migrateCosmetic = (item: string) => item === "birthday hat" ? "party hat" : item;
  const currentMinigame = value.currentMinigame ? { ...value.currentMinigame, roundClaims: value.currentMinigame.roundClaims || {}, seed: value.currentMinigame.seed || 0 } : null;
  return {
    ...fresh, ...value,
    settings,
    careMeter: clamp(Number(value.careMeter ?? legacy.birthdayMeter ?? 0)),
    pets: Object.fromEntries(Object.entries(value.pets || {}).map(([playerId, pet]) => [playerId, {
      ...pet,
      inventory: (pet.inventory || []).map(migrateCosmetic), equipped: normalizeEquipped((pet.equipped || []).map(migrateCosmetic)), traits: pet.traits || {}, relationships: pet.relationships || {}, achievements: pet.achievements || [],
      lastMissionClaimAt: pet.lastMissionClaimAt || 0,
      mission: pet.mission ? { ...pet.mission, realWorld: Boolean(pet.mission.realWorld), safety: pet.mission.safety || "" } : null,
      statistics: { ...freshStatistics(), ...(pet.statistics || {}) },
    }])),
    events: value.events || [], reachedMilestones: value.reachedMilestones || [], awards: value.awards || [], currentMinigame,
  };
}

export function createPartyPet(state: PartyPetsState, playerId: string, playerName: string, input: { name?: unknown; creature?: unknown; color?: unknown }, now = Date.now()) {
  const existing = state.pets[playerId];
  if (existing) return existing;
  const creature = PARTY_PET_CREATURES.includes(input.creature as PartyPetCreature) ? input.creature as PartyPetCreature : "blob";
  const name = clean(input.name, 20) || `${clean(playerName, 16) || "Party"}'s Pet`;
  const color = /^#[0-9a-f]{6}$/i.test(String(input.color || "")) ? String(input.color) : "#ff7c8b";
  const pet: PartyPet = {
    id: id("pet", now), playerId, name, creature, color, createdAt: now, lastActionAt: now, lastPlayerActionAt: now,
    mood: "Needs a little care", hunger: 30, energy: 30, fun: 30, social: 30, chaos: 20, coins: 20,
    inventory: ["party hat"], equipped: [], traits: { curiosity: 2, confidence: 1, mischief: 0, affection: 1 }, relationships: {}, achievements: ["Freshly Hatched"], evolution: null,
    mission: newMission(state.settings.realWorldMissions), lastMissionClaimAt: 0, action: { kind: "celebrate", label: "making a dramatic entrance", targetPetId: null, x: 12 + Math.random() * 76, y: 52 + Math.random() * 30, until: now + 6_000 },
    statistics: freshStatistics(), favoriteFood: null, lastNotableEvent: `${name} arrived in the pet world!`,
  };
  state.pets[playerId] = pet;
  addEvent(state, { type: "pet_joined", text: `${name} has arrived!`, actorPetId: pet.id, major: true }, now);
  return pet;
}

function newMission(realWorld = false): PartyPetMission {
  if (realWorld) {
    const text = PARTY_PET_REAL_WORLD_MISSIONS[Math.floor(Math.random() * PARTY_PET_REAL_WORLD_MISSIONS.length)];
    return { id: id("mission"), text, kind: "realWorld", target: 1, progress: 0, realWorld: true, safety: REAL_WORLD_SAFETY };
  }
  const source = MISSIONS[Math.floor(Math.random() * MISSIONS.length)];
  return { id: id("mission"), text: source[1], kind: source[2], target: source[3], progress: 0, realWorld: false, safety: "" };
}

function addEvent(state: PartyPetsState, event: Omit<PartyPetEvent, "id" | "createdAt">, now = Date.now()) {
  state.events.push({ ...event, id: id("event", now), createdAt: now });
  if (state.events.length > 60) state.events.splice(0, state.events.length - 60);
  const actor = activePartyPets(state).find((pet) => pet.id === event.actorPetId);
  if (actor) actor.lastNotableEvent = event.text;
}

function relationship(pet: PartyPet, target: PartyPet) {
  return pet.relationships[target.id] ||= { familiarity: 0, friendship: 0, rivalry: 0, admiration: 0, annoyance: 0, trust: 0, label: "Acquaintances" };
}

function updateRelationship(state: PartyPetsState, pet: PartyPet, target: PartyPet, friendship: number, rivalry: number, now: number) {
  if (!state.settings.relationshipsEnabled) return;
  const rel = relationship(pet, target);
  const reverse = relationship(target, pet);
  rel.familiarity = clamp(rel.familiarity + 6);
  reverse.familiarity = clamp(reverse.familiarity + 4);
  rel.friendship = clamp(rel.friendship + friendship);
  reverse.friendship = clamp(reverse.friendship + Math.max(1, friendship - 1));
  rel.rivalry = clamp(rel.rivalry + rivalry);
  reverse.rivalry = clamp(reverse.rivalry + Math.max(0, rivalry - 1));
  const old = rel.label;
  rel.label = rel.friendship >= 65 ? "Best Friends" : rel.rivalry >= 55 ? "Rivals" : rel.friendship >= 30 ? "Friends" : rel.rivalry >= 25 ? "Frenemies" : "Acquaintances";
  reverse.label = rel.label;
  if (rel.label !== old && ["Best Friends", "Rivals"].includes(rel.label)) {
    if (rel.label === "Best Friends") { pet.statistics.friendshipsFormed += 1; target.statistics.friendshipsFormed += 1; }
    else { pet.statistics.rivalriesFormed += 1; target.statistics.rivalriesFormed += 1; }
    addEvent(state, { type: rel.label === "Best Friends" ? "best_friends" : "rivals", text: rel.label === "Best Friends" ? `${pet.name} and ${target.name} are now Best Friends.` : `${pet.name} has declared ${target.name} its Rival.`, actorPetId: pet.id, targetPetId: target.id, major: true }, now);
  }
}

function increaseMeter(state: PartyPetsState, amount: number, now: number) {
  state.careMeter = clamp(state.careMeter + amount);
  for (const milestone of [25, 50, 75, 90, 100]) {
    if (state.careMeter >= milestone && !state.reachedMilestones.includes(milestone)) {
      state.reachedMilestones.push(milestone);
      const text = milestone === 25 ? "Care Power hit 25% — bonus bubbles!" : milestone === 50 ? "Care Power hit 50% — party hats for every pet!" : milestone === 75 ? "Care Power hit 75% — spontaneous happy dance!" : milestone === 90 ? "Care Power hit 90% — the deluxe habitat is glowing." : "CARE POWER MAXIMUM — the grand finale is unlocked!";
      addEvent(state, { type: `meter_${milestone}`, text, major: true }, now);
      if (milestone === 50) activePartyPets(state).forEach((pet) => { if (!pet.equipped.includes("party hat")) equipCosmetic(pet, "party hat"); });
    }
  }
}

function trait(pet: PartyPet, key: string, amount = 1) { pet.traits[key] = (pet.traits[key] || 0) + amount; }

function updateMission(state: PartyPetsState, pet: PartyPet, kind: keyof PartyPetStats) {
  if (!pet.mission || pet.mission.kind !== kind) return false;
  pet.mission.progress = Math.min(pet.mission.target, pet.statistics[kind]);
  if (pet.mission.progress < pet.mission.target) return false;
  pet.statistics.missionsCompleted += 1;
  pet.coins += 12;
  if (!pet.achievements.includes("Mission Possible")) pet.achievements.push("Mission Possible");
  pet.mission = newMission(state.settings.realWorldMissions);
  return true;
}

export function completePartyPetRealWorldMission(state: PartyPetsState, playerId: string, missionId: unknown, now = Date.now()) {
  if (state.status !== "live" || !state.settings.realWorldMissions) return false;
  const pet = state.pets[playerId];
  const submittedId = clean(missionId, 80);
  if (pet?.sittingOut || !pet?.mission?.realWorld || pet.mission.id !== submittedId || now - pet.lastMissionClaimAt < 30_000) return false;
  pet.lastMissionClaimAt = now;
  pet.statistics.missionsCompleted += 1;
  pet.statistics.realWorldMissionsCompleted += 1;
  pet.coins += 18;
  pet.social = clamp(pet.social + 10);
  trait(pet, "socialButterfly", 2);
  if (!pet.achievements.includes("Friendly Face")) pet.achievements.push("Friendly Face");
  pet.mission = newMission(true);
  increaseMeter(state, 3, now);
  addEvent(state, { type: "real_world_mission", text: `${pet.name} completed a friendly real-world mission. A new acquaintance has entered the lore.`, actorPetId: pet.id }, now);
  return true;
}

export function performPartyPetAction(state: PartyPetsState, playerId: string, input: { action?: unknown; item?: unknown; targetId?: unknown }, now = Date.now()) {
  if (!['live', 'minigame'].includes(state.status) || state.currentMinigame) return false;
  const pet = state.pets[playerId];
  if (!pet || pet.sittingOut || now - pet.lastPlayerActionAt < 900) return false;
  const action = clean(input.action, 24);
  const item = clean(input.item, 30);
  const target = activePartyPets(state).find((candidate) => candidate.id === input.targetId && candidate.id !== pet.id);
  let text = "";
  if (action === "feed" && FOOD.includes(item)) {
    const price = FOOD_PRICES[item]; if (pet.coins < price) return false; pet.coins -= price;
    const hungerBoost = item === "tacos" ? 28 : item === "pizza" ? 26 : item === "cake" ? 20 : 18;
    const energyBoost = item === "cake" ? 15 : item === "mystery snack" ? 10 : item === "tacos" ? 8 : 6;
    pet.hunger = clamp(pet.hunger + hungerBoost); pet.energy = clamp(pet.energy + energyBoost); pet.fun = clamp(pet.fun + (item === "cake" || item === "mystery snack" ? 10 : 5)); pet.statistics.foodsEaten += 1;
    if (item === "cake") { pet.statistics.cakeEaten += 1; trait(pet, "snackMonster", 2); pet.chaos = clamp(pet.chaos + 8); }
    else trait(pet, "snackMonster");
    pet.favoriteFood = item; pet.mood = item === "spicy pepper" ? "Cartoon fire incoming" : "Deliciously smug";
    pet.action = { ...pet.action, kind: "eat", label: `eating ${item}`, targetPetId: null, until: now + 5_000 };
    text = item === "spicy pepper" ? `${pet.name} ate a spicy pepper and is now legally a tiny dragon.` : `${pet.name} demolished the ${item}. No crumbs survived.`;
    updateMission(state, pet, "foodsEaten");
  } else if (action === "play" && TOYS.includes(item)) {
    const price = TOY_PRICES[item]; if (pet.coins < price) return false; pet.coins -= price;
    pet.fun = clamp(pet.fun + (item === "karaoke" ? 30 : 25)); pet.social = clamp(pet.social + (item === "karaoke" || item === "balloons" ? 10 : 7)); pet.energy = clamp(pet.energy - 3); pet.mood = "Having an unreasonable amount of fun"; trait(pet, item === "karaoke" ? "partyAnimal" : "classClown");
    if (item === "balloons") pet.statistics.balloonsPopped += 1;
    pet.action = { ...pet.action, kind: item === "karaoke" ? "dance" : "play", label: `playing with ${item}`, targetPetId: null, until: now + 6_000 };
    text = `${pet.name} is ${item === "karaoke" ? "performing karaoke with enormous confidence and no range" : `playing with ${item}`}.`;
  } else if (action === "dress" && COSMETICS.includes(item)) {
    const puttingOn = !pet.equipped.includes(item);
    if (!pet.inventory.includes(item)) pet.inventory.push(item);
    if (puttingOn) { equipCosmetic(pet, item); pet.coins += DRESS_REWARD; }
    else pet.equipped = pet.equipped.filter((entry) => entry !== item);
    pet.statistics.outfitsWorn += 1; trait(pet, "fashionIcon", 2); pet.mood = "Serving a look";
    pet.action = { ...pet.action, kind: "celebrate", label: `modeling ${item}`, targetPetId: null, until: now + 5_000 };
    text = puttingOn ? `${pet.name} put on ${item} and earned ${DRESS_REWARD} coins. Fashion pays!` : `${pet.name} took off ${item} to plan the next look.`; updateMission(state, pet, "outfitsWorn");
  } else if (action === "gift" && target && GIFTS.includes(item)) {
    pet.statistics.giftsGiven += 1; target.statistics.giftsReceived += 1; pet.social = clamp(pet.social + 10); target.fun = clamp(target.fun + 9); trait(pet, "giftGiver", 2);
    updateRelationship(state, pet, target, 12, 0, now); pet.action = { ...pet.action, kind: "gift", label: `giving ${item}`, targetPetId: target.id, x: target.action.x - 3, y: target.action.y, until: now + 6_000 };
    text = `${pet.name} gave ${target.name} a ${item}. Their tiny society approves.`; updateMission(state, pet, "giftsGiven");
  } else if (["wave", "hug", "dance", "high five", "share food", "challenge", "tell joke"].includes(action) && target) {
    pet.statistics.interactions += 1; target.statistics.interactions += 1; pet.social = clamp(pet.social + 12); target.social = clamp(target.social + 7);
    if (action === "dance") { pet.statistics.dances += 1; target.statistics.dances += 1; trait(pet, "partyAnimal", 2); updateMission(state, pet, "dances"); }
    updateRelationship(state, pet, target, action === "challenge" ? 2 : 8, action === "challenge" ? 10 : 0, now);
    pet.action = { ...pet.action, kind: action === "dance" ? "dance" : "visit", label: `${action} with ${target.name}`, targetPetId: target.id, x: target.action.x - 4, y: target.action.y, until: now + 6_000 };
    text = action === "tell joke" ? `${pet.name} told ${target.name} a joke. ${target.name} is still processing it.` : `${pet.name} and ${target.name} ${action === "high five" ? "attempted a high five" : action + "d"}.`;
    updateMission(state, pet, "interactions");
  } else if (["steal hat", "steal snack", "photobomb", "pop balloon", "spray confetti", "dance battle", "hide toy"].includes(action) && target && state.settings.mischiefEnabled) {
    pet.statistics.pranks += 1; target.statistics.timesPhotobombed += action === "photobomb" ? 1 : 0; pet.chaos = clamp(pet.chaos + 12); target.fun = clamp(target.fun + 4); trait(pet, "prankster", 2);
    updateRelationship(state, pet, target, 2, 9, now); pet.action = { ...pet.action, kind: "prank", label: `${action} on ${target.name}`, targetPetId: target.id, x: target.action.x + 4, y: target.action.y, until: now + 6_000 };
    text = `${pet.name} tried to ${action} ${target.name}. It was extremely subtle if nobody looked.`; updateMission(state, pet, "pranks");
  } else return false;
  pet.lastPlayerActionAt = now; pet.lastActionAt = now;
  increaseMeter(state, target ? 2 : 1, now);
  addEvent(state, { type: action, text, actorPetId: pet.id, targetPetId: target?.id, object: item }, now);
  evaluatePet(state, pet, now);
  return true;
}

function strongestTraits(pet: PartyPet) {
  const labels: Record<string, string> = { partyAnimal: "Party Animal", prankster: "Chaos Gremlin", giftGiver: "Gift Giver", fashionIcon: "Fashion Icon", snackMonster: "Snack Monster", classClown: "Class Clown", sleepyhead: "Sleepyhead", socialButterfly: "Social Butterfly", competitive: "Competitive Maniac", curiosity: "Curious Creature" };
  return Object.entries(pet.traits).sort((a, b) => b[1] - a[1]).filter(([key]) => labels[key]).slice(0, 3).map(([key]) => labels[key]);
}

function evaluatePet(state: PartyPetsState, pet: PartyPet, now: number) {
  if (pet.social >= 85) trait(pet, "socialButterfly");
  if (pet.energy <= 25) trait(pet, "sleepyhead");
  if (!state.settings.evolutionsEnabled || pet.evolution) return;
  let evolution: string | null = null; let reason = "";
  if (pet.statistics.cakeEaten >= 7) { evolution = "Sugar Goblin"; reason = `${pet.statistics.cakeEaten} pieces of cake later...`; }
  else if (pet.statistics.giftsGiven >= 8) { evolution = "Golden Heart"; reason = "A suspicious amount of generosity..."; }
  else if (pet.statistics.pranks >= 7) { evolution = "Chaos Gremlin"; reason = "The prank ledger could no longer contain it..."; }
  else if (pet.statistics.outfitsWorn >= 8) { evolution = "Runway Monster"; reason = "Six hats were only the beginning..."; }
  else if (pet.statistics.dances >= 9) { evolution = "Party Animal"; reason = "The dance floor chose its champion..."; }
  else if (now - pet.lastPlayerActionAt > 50 * 60_000 && state.startedAt && now - state.startedAt > 50 * 60_000) { evolution = "Feral Party Beast"; reason = "Left unsupervised, it adapted..."; }
  if (evolution) {
    pet.evolution = evolution; pet.mood = `Evolved into ${evolution}`; pet.action = { ...pet.action, kind: "celebrate", label: "EVOLVING", until: now + 10_000 };
    if (!pet.achievements.includes("This Isn't Even My Final Form")) pet.achievements.push("This Isn't Even My Final Form");
    addEvent(state, { type: "evolution", text: `${pet.name} is evolving... ${reason} ${pet.name} has become a ${evolution}!`, actorPetId: pet.id, major: true }, now);
  }
}

function randomPair(state: PartyPetsState) {
  const pets = activePartyPets(state);
  if (pets.length < 2) return [pets[0], undefined] as const;
  const first = pets[Math.floor(Math.random() * pets.length)];
  let second = pets[Math.floor(Math.random() * pets.length)];
  if (second.id === first.id) second = pets[(pets.indexOf(first) + 1) % pets.length];
  return [first, second] as const;
}

export function tickPartyPets(state: PartyPetsState, now = Date.now()) {
  if (!['live', 'minigame'].includes(state.status)) return false;
  let changed = false;
  if (state.currentMinigame && now >= state.currentMinigame.endsAt) { finishPartyPetMinigame(state, now); changed = true; }
  if (now >= state.nextAutonomyAt && !state.currentMinigame) {
    const [pet, target] = randomPair(state);
    if (pet) {
      pet.hunger = clamp(pet.hunger - 2); pet.energy = clamp(pet.energy - 1); pet.fun = clamp(pet.fun - 1); pet.social = clamp(pet.social - 1);
      const lonely = now - pet.lastPlayerActionAt > 20 * 60_000;
      const roll = Math.random();
      if (pet.energy < 28 || roll < .12) {
        pet.energy = clamp(pet.energy + 12); pet.statistics.naps += 1; trait(pet, "sleepyhead"); pet.mood = "Napping through history";
        pet.action = { ...pet.action, kind: "sleep", label: roll < .04 ? "sleeping in the cake zone" : "taking a power nap", targetPetId: null, until: now + 14_000 };
        addEvent(state, { type: "nap", text: `${pet.name} fell asleep ${roll < .04 ? "directly in the middle of the dance floor" : "during a perfectly good party"}.`, actorPetId: pet.id }, now);
      } else if (target && roll < .48) {
        const socialRoll = Math.random();
        if (socialRoll < .2 && state.settings.mischiefEnabled) {
          pet.statistics.pranks += 1; pet.chaos = clamp(pet.chaos + 5); trait(pet, "prankster"); updateRelationship(state, pet, target, 1, 5, now);
          pet.action = { ...pet.action, kind: "prank", label: `plotting near ${target.name}`, targetPetId: target.id, x: target.action.x + 5, y: target.action.y, until: now + 10_000 };
          addEvent(state, { type: "autonomous_prank", text: `${pet.name} put a tiny hat on ${target.name}, then pretended this was normal.`, actorPetId: pet.id, targetPetId: target.id }, now);
        } else {
          pet.statistics.interactions += 1; pet.social = clamp(pet.social + 4); trait(pet, socialRoll > .78 ? "partyAnimal" : "socialButterfly"); updateRelationship(state, pet, target, 4, 0, now);
          const behavior = socialRoll > .78 ? "started a two-pet dance circle with" : socialRoll > .55 ? "is explaining balloons to" : "wandered over to gossip with";
          pet.action = { ...pet.action, kind: socialRoll > .78 ? "dance" : "visit", label: `${behavior} ${target.name}`, targetPetId: target.id, x: target.action.x - 5, y: target.action.y, until: now + 10_000 };
          addEvent(state, { type: "autonomous_social", text: `${pet.name} ${behavior} ${target.name}.`, actorPetId: pet.id, targetPetId: target.id }, now);
        }
      } else if (pet.hunger < 40 || roll < .68) {
        pet.mood = pet.hunger <= 15 ? "Very hungry and deeply offended" : "Staring longingly at the snack table";
        pet.action = { ...pet.action, kind: "idle", label: pet.hunger <= 15 ? "dramatically begging for a snack" : "planning a snack purchase", targetPetId: null, x: 78 + Math.random() * 9, y: 65 + Math.random() * 8, until: now + 9_000 };
        addEvent(state, { type: "needs_food", text: `${pet.name} is staring at the snack table. Care items cost coins—time for a real-world mission!`, actorPetId: pet.id }, now);
      } else {
        pet.action = { ...pet.action, kind: roll > .9 ? "dance" : "walk", label: lonely ? "developing an elaborate balloon theory" : "investigating the party", targetPetId: null, x: 8 + Math.random() * 84, y: 44 + Math.random() * 42, until: now + 10_000 };
        if (lonely) { pet.chaos = clamp(pet.chaos + 3); pet.mood = "Unsupervised and thriving"; }
      }
      evaluatePet(state, pet, now); changed = true;
    }
    state.nextAutonomyAt = now + 10_000 + Math.floor(Math.random() * 8_000);
  }
  if (now >= state.nextPartyEventAt && !state.currentMinigame) {
    const minigameChance = state.settings.minigameFrequency === "high" ? .7 : state.settings.minigameFrequency === "low" ? .25 : .45;
    if (activePartyPets(state).length >= 2 && Math.random() < minigameChance) {
      const games: PartyPetMinigameKind[] = ["pet_race", "dance_battle", "balloon_pop", "cake_grab", "musical_chairs", "pet_talent_show", "hot_potato", "simon_says", "reaction_race", "snack_catch", "majority_rules", "chaos_round"];
      startPartyPetMinigame(state, games[Math.floor(Math.random() * games.length)], now);
      state.nextPartyEventAt = now + state.settings.eventPaceMinutes * 60_000;
    } else triggerPartyPetEvent(state, undefined, now);
    changed = true;
  }
  return changed;
}

const MAJOR_EVENTS = [
  ["pizza_delivery", "PIZZA DELIVERY! Every pet has forgotten its manners."], ["balloon_storm", "BALLOON STORM! The ceiling has made a terrible decision."],
  ["lights_out", "LIGHTS OUT! Tiny flashlights. Extremely suspicious whispers."], ["cake_emergency", "SNACK EMERGENCY! Someone left tiny footprints near the treat bowl."],
  ["alien_visit", "ALIEN VISIT! The pets are pretending this happens all the time."], ["pet_parade", "PET PARADE! Everybody walk like the room is a runway."],
  ["karaoke", "KARAOKE! Confidence is high. Musical accuracy is optional."], ["snack_stampede", "SNACK STAMPEDE! Please remain calm. The pets will not."],
] as const;

export function triggerPartyPetEvent(state: PartyPetsState, requested?: unknown, now = Date.now()) {
  const found = MAJOR_EVENTS.find(([key]) => key === requested) || MAJOR_EVENTS[Math.floor(Math.random() * MAJOR_EVENTS.length)];
  addEvent(state, { type: found[0], text: found[1], major: true }, now);
  activePartyPets(state).forEach((pet, index) => { pet.action = { ...pet.action, kind: found[0] === "lights_out" ? "shocked" : found[0] === "pizza_delivery" ? "eat" : "celebrate", label: found[1], targetPetId: null, x: 15 + ((index * 17) % 70), y: 50 + ((index * 13) % 30), until: now + 12_000 }; });
  increaseMeter(state, Math.max(2, Math.min(7, activePartyPets(state).length)), now);
  state.nextPartyEventAt = now + state.settings.eventPaceMinutes * 60_000;
  return found[0];
}

export function updatePartyPetSettings(state: PartyPetsState, input: Record<string, unknown>, now = Date.now()) {
  const realWorldWasEnabled = state.settings.realWorldMissions;
  if (["playroom", "disco", "garden", "space"].includes(String(input.theme))) state.settings.theme = input.theme as PartyPetTheme;
  if (["family", "standard", "adult"].includes(String(input.tone))) state.settings.tone = input.tone as PartyPetTone;
  if ([6, 12, 18].includes(Number(input.eventPaceMinutes))) state.settings.eventPaceMinutes = Number(input.eventPaceMinutes);
  if (["low", "normal", "high"].includes(String(input.minigameFrequency))) state.settings.minigameFrequency = input.minigameFrequency as PartyPetSettings["minigameFrequency"];
  for (const key of ["soundEnabled", "realWorldMissions", "relationshipsEnabled", "mischiefEnabled", "evolutionsEnabled"] as const) if (typeof input[key] === "boolean") state.settings[key] = input[key];
  if (state.settings.realWorldMissions !== realWorldWasEnabled) {
    activePartyPets(state).forEach((pet) => {
      if (!pet.mission || pet.mission.progress === 0) pet.mission = newMission(state.settings.realWorldMissions);
    });
  }
  state.nextPartyEventAt = Math.min(state.nextPartyEventAt, now + state.settings.eventPaceMinutes * 60_000);
}

export function startPartyPets(state: PartyPetsState, now = Date.now()) {
  if (!activePartyPets(state).length) return false;
  state.status = "live"; state.startedAt ||= now; state.nextAutonomyAt = now + 4_000; state.nextPartyEventAt = now + state.settings.eventPaceMinutes * 60_000;
  addEvent(state, { type: "party_started", text: "THE PET WORLD IS LIVE! The miniature society has been left unsupervised.", major: true }, now);
  return true;
}

export function startPartyPetMinigame(state: PartyPetsState, kind: unknown, now = Date.now()) {
  if (!Object.prototype.hasOwnProperty.call(MINIGAME_TITLES, String(kind)) || state.currentMinigame || !activePartyPets(state).length) return false;
  const valid = kind as PartyPetMinigameKind; const [title, instruction, duration] = MINIGAME_TITLES[valid];
  state.status = "minigame"; state.currentMinigame = { id: id("minigame", now), kind: valid, title, instruction, startedAt: now + 3_000, endsAt: now + duration, scores: {}, inputCounts: {}, lastInputAt: {}, selections: {}, roundClaims: {}, seed: Math.floor(Math.random() * 10_000), winnerIds: [], resultText: "" };
  activePartyPets(state).forEach((pet) => { pet.statistics.minigameParticipation += 1; pet.action = { ...pet.action, kind: "celebrate", label: `getting ready for ${title}`, until: now + 5_000 }; });
  addEvent(state, { type: "minigame_start", text: `${title.toUpperCase()} begins now! ${instruction}`, major: true }, now);
  return true;
}

export function applyPartyPetMinigameInput(state: PartyPetsState, playerId: string, value: unknown, now = Date.now()) {
  const game = state.currentMinigame; const pet = state.pets[playerId]; if (!game || !pet || pet.sittingOut || now < game.startedAt || now >= game.endsAt) return false;
  const selection = clean(value, 40);
  if (["fashion_show", "pet_talent_show", "majority_rules", "chaos_round"].includes(game.kind)) {
    const choices: Partial<Record<PartyPetMinigameKind, readonly string[]>> = {
      fashion_show: COSMETICS.slice(0, 9),
      pet_talent_show: ["magic", "comedy", "juggling", "singing", "dramatic monologue", "weird noise"],
      chaos_round: ["throw confetti", "jump", "steal snacks", "hide", "dance", "swap hats"],
    };
    if (!selection || (game.kind === "majority_rules" ? !state.pets[selection] || state.pets[selection].sittingOut || selection === playerId : !choices[game.kind]?.includes(selection))) return false;
    game.selections[playerId] = selection; game.scores[playerId] = 1; return true;
  }
  const elapsed = now - game.startedAt;
  const round = (length: number) => Math.floor(elapsed / length);
  const claimRound = (key: string, value: number) => {
    const claim = `${key}:${value}`;
    if (game.roundClaims[playerId] === claim) return false;
    game.roundClaims[playerId] = claim;
    return true;
  };
  const last = game.lastInputAt[playerId] || 0; if (now - last < 120) return false;
  const delta = now - last; game.lastInputAt[playerId] = now; game.inputCounts[playerId] = (game.inputCounts[playerId] || 0) + 1;
  let points = 1;
  if (game.kind === "pet_race") {
    const expected = (game.inputCounts[playerId] || 1) % 2 ? "left" : "right";
    points = selection === expected ? (delta >= 180 && delta <= 900 ? 3 : 2) : -2;
  }
  if (game.kind === "dance_battle") {
    const beat = round(900); if (!claimRound("dance", beat)) return false;
    points = selection === ["pink", "gold", "mint", "purple"][(beat + game.seed) % 4] ? 4 : -1;
  }
  if (game.kind === "balloon_pop") {
    const bunch = round(1_500); if (!claimRound("balloon", bunch)) return false;
    const match = /^balloon:(\d+):(\d+)$/.exec(selection); const picked = Number(match?.[2]);
    points = Number(match?.[1]) === bunch && picked === (bunch + game.seed) % 6 ? 5 : -1;
    if (points > 0) pet.statistics.balloonsPopped += 1;
  }
  if (game.kind === "cake_grab") {
    const treatRound = round(4_000); if (!claimRound("treat", treatRound)) return false;
    const phase = elapsed % 4_000; points = selection === "grab" && phase >= 2_200 && phase <= 3_150 ? Math.max(2, 8 - Math.floor((phase - 2_200) / 180)) : -2;
    if (points > 0) pet.statistics.cakeEaten += 1;
  }
  if (game.kind === "musical_chairs") {
    const chairRound = round(5_000); if (!claimRound("chair", chairRound)) return false;
    const phase = elapsed % 5_000; points = /^chair:[0-2]$/.test(selection) && phase >= 3_300 && phase <= 4_250 ? Math.max(2, 7 - Math.floor((phase - 3_300) / 190)) : -2;
  }
  if (game.kind === "hot_potato") {
    const passRound = round(1_100); if (!claimRound("pass", passRound)) return false;
    points = selection === `pass:${(passRound + game.seed) % 2 ? "right" : "left"}` ? 3 : -1;
  }
  if (game.kind === "simon_says") {
    const commandRound = round(1_600); if (!claimRound("simon", commandRound)) return false;
    const commands = ["jump", "dance", "duck", "spin", "clap"]; const simon = (commandRound + game.seed) % 4 !== 3;
    const expected = commands[(commandRound + game.seed) % commands.length]; points = simon && selection === expected ? 4 : -2;
  }
  if (game.kind === "reaction_race") {
    const signalRound = round(4_000); if (!claimRound("reaction", signalRound)) return false;
    const signalPhase = elapsed % 4_000; points = signalPhase >= 2_300 && signalPhase <= 3_150 ? Math.max(2, 7 - Math.floor((signalPhase - 2_300) / 170)) : -2;
  }
  if (game.kind === "snack_catch") {
    const snackRound = round(1_700); if (!claimRound("snack", snackRound)) return false;
    const lane = (snackRound * 2 + game.seed) % 3; const yuck = (snackRound + game.seed) % 5 === 4;
    points = selection === `lane:${lane}` ? (yuck ? -3 : 4) : (yuck ? 1 : -1);
  }
  game.scores[playerId] = Math.max(0, (game.scores[playerId] || 0) + points); return true;
}

export function finishPartyPetMinigame(state: PartyPetsState, now = Date.now(), endedEarly = false) {
  const game = state.currentMinigame; if (!game) return false;
  if (game.kind === "majority_rules") {
    const counts: Record<string, number> = {}; Object.values(game.selections).forEach((choice) => counts[choice] = (counts[choice] || 0) + 1);
    const winner = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0]; if (winner) game.scores[winner] = (game.scores[winner] || 0) + 5;
  } else if (["fashion_show", "pet_talent_show", "chaos_round"].includes(game.kind)) {
    const counts: Record<string, number> = {}; Object.values(game.selections).forEach((choice) => counts[choice] = (counts[choice] || 0) + 1);
    Object.entries(game.selections).forEach(([playerId, choice]) => game.scores[playerId] = Math.max(2, 10 - (counts[choice] - 1) * 2));
  }
  const best = Math.max(0, ...Object.values(game.scores)); game.winnerIds = Object.keys(game.scores).filter((playerId) => game.scores[playerId] === best && best > 0);
  game.winnerIds.forEach((playerId) => { const pet = state.pets[playerId]; if (!pet) return; pet.statistics.minigameWins += 1; pet.coins += 15; trait(pet, "competitive", 2); pet.action = { ...pet.action, kind: "celebrate", label: `${game.title} champion`, until: now + 9_000 }; if (!pet.achievements.includes("Tiny Champion")) pet.achievements.push("Tiny Champion"); });
  const names = game.winnerIds.map((playerId) => state.pets[playerId]?.name).filter(Boolean);
  game.resultText = names.length
    ? `${names.join(" & ")} won ${game.title}${endedEarly ? " on the early scoreboard" : ""}!${endedEarly ? " All earned points and prizes were awarded." : ""}`
    : `${game.title} ended${endedEarly ? " early" : ""} in beautiful confusion.${endedEarly ? " All earned points were kept." : ""}`;
  addEvent(state, { type: "minigame_end", text: game.resultText, actorPetId: game.winnerIds[0] ? state.pets[game.winnerIds[0]]?.id : undefined, major: true }, now);
  increaseMeter(state, Math.max(4, game.winnerIds.length * 3), now); state.status = "live"; state.currentMinigame = null; state.nextAutonomyAt = now + 7_000; return true;
}

export function cancelPartyPetMinigame(state: PartyPetsState, now = Date.now()) {
  return finishPartyPetMinigame(state, now, true);
}

export function rewardPartyPet(state: PartyPetsState, playerId: string, reward: { coins?: number; achievement?: string; cosmetic?: string; source?: string }, now = Date.now()) {
  const pet = state.pets[playerId]; if (!pet) return false;
  pet.coins += clamp(Number(reward.coins || 0), 0, 1000);
  const achievement = clean(reward.achievement, 50); if (achievement && !pet.achievements.includes(achievement)) pet.achievements.push(achievement);
  const cosmetic = clean(reward.cosmetic, 30); if (cosmetic && !pet.inventory.includes(cosmetic)) pet.inventory.push(cosmetic);
  if (cosmetic.includes("crown") && !pet.equipped.includes(cosmetic)) equipCosmetic(pet, cosmetic);
  addEvent(state, { type: "cross_game_reward", text: `${pet.name} earned ${achievement || cosmetic || `${reward.coins || 0} coins`}${reward.source ? ` in ${clean(reward.source, 30)}` : ""}!`, actorPetId: pet.id }, now); return true;
}

export function setPartyPetMeter(state: PartyPetsState, amount: unknown, now = Date.now()) {
  if (!Number.isFinite(Number(amount))) return false; const next = clamp(Number(amount)); const delta = next - state.careMeter; if (delta > 0) increaseMeter(state, delta, now); else state.careMeter = next; return true;
}

export function createPartyPetBots(state: PartyPetsState, count: number, now = Date.now()) {
  const names = ["Beans", "Pickles", "Mochi", "Nugget", "Waffles", "Gizmo", "Kevin Jr.", "Pesto", "Toast", "Biscuit", "Socks", "Crouton", "Pebble", "Miso", "Bongo", "Tater"];
  const total = Math.max(0, Math.min(20, Math.floor(count)));
  for (let index = 0; index < total; index += 1) {
    const playerId = `bot-party-pet-${index}`; if (state.pets[playerId]) continue;
    createPartyPet(state, playerId, names[index % names.length], { name: names[index % names.length], creature: PARTY_PET_CREATURES[index % PARTY_PET_CREATURES.length], color: ["#ff7c8b", "#70dfc0", "#ffd45f", "#9f82ff", "#69c9ff", "#ff9c55"][index % 6] }, now + index);
  }
  return total;
}

export function buildPartyPetAwards(state: PartyPetsState) {
  const pets = activePartyPets(state); const awards: PartyPetsState["awards"] = [];
  const leader = (key: keyof PartyPetStats) => [...pets].sort((a, b) => b.statistics[key] - a.statistics[key])[0];
  const add = (title: string, pet: PartyPet | undefined, key: keyof PartyPetStats, reason: string) => { if (pet && pet.statistics[key] > 0) awards.push({ title, petIds: [pet.id], reason: `${pet.statistics[key]} ${reason}` }); };
  add("Agent of Chaos", leader("pranks"), "pranks", "harmless pranks"); add("Most Social", leader("interactions"), "interactions", "pet interactions"); add("Friendliest Stranger", leader("realWorldMissionsCompleted"), "realWorldMissionsCompleted", "friendly new-person missions"); add("Biggest Snack Monster", leader("foodsEaten"), "foodsEaten", "snacks demolished"); add("Most Generous", leader("giftsGiven"), "giftsGiven", "gifts sent"); add("Dance Champion", leader("dances"), "dances", "dance incidents"); add("Sleepiest", leader("naps"), "naps", "public naps"); add("Tiny Champion", leader("minigameWins"), "minigameWins", "minigame wins");
  state.awards = awards.slice(0, 8); return state.awards;
}

export function startPartyPetFinale(state: PartyPetsState, now = Date.now()) {
  buildPartyPetAwards(state); state.status = "finale"; state.finalPhotoAt = now + 15_000;
  activePartyPets(state).forEach((pet, index) => { pet.action = { ...pet.action, kind: "celebrate", label: "posing for the group photo", targetPetId: null, x: 20 + ((index * 13) % 65), y: 55 + (index % 3) * 10, until: now + 30_000 }; });
  addEvent(state, { type: "finale", text: "FINAL GROUP PHOTO! Strike a pose. Try not to eat the camera.", major: true }, now); return true;
}

export function publicPartyPetsState(state: PartyPetsState, viewerId?: string, isDisplay = false) {
  return {
    ...state,
    pets: Object.fromEntries(Object.entries(state.pets).filter(([, pet]) => !pet.sittingOut).map(([playerId, pet]) => [playerId, { ...pet, mission: !isDisplay && playerId === viewerId ? pet.mission : null, traits: Object.fromEntries(strongestTraits(pet).map((label, index) => [label, 3 - index])) }])),
  };
}
